import {
  Box,
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Button,
  Tooltip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Divider
} from '@mui/material';
import { useEffect, useState } from 'react';
import { api } from '../../api';
import { ActiveStatus, OnlineSafetyTraining } from '../../schema/ptt';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DescriptionIcon from '@mui/icons-material/Description';
import dayjs from 'dayjs';

interface OnlineSafetyTrainingProps {
  person_id?: string | number;
  editable?: boolean;
  reloadStatus?: () => void;
}

const OnlineSafetyTrainingPage: React.FC<OnlineSafetyTrainingProps> = ({
  person_id,
  editable,
  reloadStatus
}: OnlineSafetyTrainingProps) => {
  const [activeStatus, setActiveStatus] = useState<ActiveStatus | null>(null);
  const [ostList, setOSTList] = useState<OnlineSafetyTraining[]>([]);
  const [isOpenSuccessDialog, setIsOpenSuccessDialog] = useState<boolean>(false);
  const [expands, setExpands] = useState<Record<number, boolean>>({});

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const toggleExpands = (index: number) => {
    const isExpand = expands[index] ? expands[index] : false;
    setExpands({ ...expands, [index]: !isExpand });
  };

  const openSuccessDialog = () => {
    setIsOpenSuccessDialog(true);
    setTimeout(() => {
      setIsOpenSuccessDialog(false);
    }, 1000);
  };
  const saveTempStatus = () => {
    if (!editable) {
      return;
    }
    if (isLoading) {
      return;
    }
    if (person_id && activeStatus) {
      setIsLoading(true);
      api
        .post(`person/${person_id}/temporary_status`, {
          is_ost_active: activeStatus.is_temp_active,
          ost_comment: activeStatus.comment
        })
        .then(() => {
          openSuccessDialog();
          reloadStatus && reloadStatus();
        })
        .finally(() => setIsLoading(false));
    }
  };

  useEffect(() => {
    if (person_id) {
      api.get(`person/${person_id}/ost`).then((res) => {
        const { data } = res;
        setOSTList(data);
      });
      api.get(`person/${person_id}/ost/status`).then((res) => {
        const { data } = res;
        setActiveStatus(data);
      });
    }
  }, [person_id]);

  return (
    <>
      <Typography variant="h6">การอนุญาต</Typography>
      {activeStatus?.is_active ? (
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="สถานะการอนุญาต"
                variant="outlined"
                placeholder="สถานะการอนุญาต"
                value={'ผ่านโดยระบบ'}
                autoComplete="off"
                InputProps={{
                  readOnly: true
                }}
              />
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              {editable ? (
                <FormControl fullWidth>
                  <InputLabel id="is_active">สถานะการอนุญาต</InputLabel>
                  <Select
                    labelId="is_active"
                    label="สถานะการอนุญาต"
                    variant="outlined"
                    onChange={(e) =>
                      activeStatus
                        ? setActiveStatus({
                            ...activeStatus,
                            is_temp_active: e.target.value == 'true'
                          })
                        : null
                    }
                    value={`${activeStatus?.is_temp_active === true}`}
                    autoComplete="off"
                    disabled={!activeStatus}
                  >
                    <MenuItem value={'true'}>ผ่าน</MenuItem>
                    <MenuItem value={'false'}>ไม่ผ่าน</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  label="สถานะการอนุญาต"
                  variant="outlined"
                  placeholder="สถานะการอนุญาต"
                  value={activeStatus?.is_temp_active ? 'ผ่าน' : 'ไม่ผ่าน'}
                  autoComplete="off"
                  InputProps={{
                    readOnly: true
                  }}
                />
              )}
            </Grid>
            <Grid item xs={9}>
              <TextField
                fullWidth
                label="หมายเหตุการอนุญาตชั่วคราวโดยเจ้าหน้าที่"
                variant="outlined"
                placeholder="หมายเหตุการอนุญาตชั่วคราวโดยเจ้าหน้าที่"
                onChange={(e) =>
                  activeStatus
                    ? setActiveStatus({ ...activeStatus, comment: e.target.value })
                    : null
                }
                value={activeStatus?.comment ? activeStatus.comment : ''}
                autoComplete="off"
                disabled={!activeStatus}
              />
            </Grid>
          </Grid>
          {editable ? (
            <Grid container columns={2} spacing={2} sx={{ mt: 0 }} direction={'row-reverse'}>
              <Grid item>
                <Button onClick={() => saveTempStatus()} variant="contained" color="primary">
                  บันทึก
                </Button>
              </Grid>
            </Grid>
          ) : null}
        </Box>
      )}
      <Divider sx={{ mt: 3 }} />
      {ostList.length == 0 ? (
        <Typography variant="h6" mt={3} sx={{ color: "red" }}>
          ไม่พบข้อมูลการอบรม Online Safety Training โปรดตรวจสอบสถานะอบรมที่ระบบ Online Safety
            {/* <Typography variant="subtitle2" mt={1} sx={{ color: "black" }}>
                กรุณาตรวจสอบข้อมูลผ่านระบบ Online Safety Training
              </Typography> */}
        </Typography>
      ) : (
        <>
          <Typography variant="h6" mt={3}>
            ข้อมูลการอบรม Online Safety Training
          </Typography>
          <Box sx={{ mt: 2 }}>
            {ostList.map((ost, index) => {
              return (
                <Card sx={{ marginBottom: 3, border: 1, borderColor: '#E5E5E5' }} key={index}>
                  <CardContent
                    sx={{
                      p: 2,
                      '&:last-child': {
                        paddingBottom: 2
                      }
                    }}
                  >
                    <Grid
                      container
                      alignItems="center"
                      onClick={() => toggleExpands(index)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <Grid item xs={10}>
                        <Typography variant="h6">
                          {ost?.CourseName ? ost?.CourseName : 'Online Safety Training'}
                        </Typography>
                      </Grid>
                      <Grid item xs={2} sx={{ textAlign: 'right' }}>
                        <IconButton onClick={() => toggleExpands(index)}>
                          {!expands[index] ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                        </IconButton>
                      </Grid>
                    </Grid>
                    <Collapse in={expands[index]}>
                      <Box sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                          {/* <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Training Course ID"
                              variant="outlined"
                              placeholder="Training Course ID"
                              value={ost?.CourseID ? ost?.CourseID : ''}
                              InputProps={{ readOnly: true }}
                              disabled={!ost?.CourseID}
                            />
                          </Grid> */}
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Training Course Name"
                              variant="outlined"
                              placeholder="Training Course Name"
                              value={ost?.CourseName ? ost?.CourseName : ''}
                              InputProps={{ readOnly: true }}
                              disabled={!ost?.CourseName}
                            />
                          </Grid>
                          <Grid item xs={6}></Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="วันเริ่มต้น Training"
                              variant="outlined"
                              placeholder="วันเริ่มต้น Training"
                              value={
                                ost?.TrainingDate
                                  ? dayjs(ost.TrainingDate).format('DD/MM/YYYY')
                                  : ''
                              }
                              InputProps={{ readOnly: true }}
                              disabled={!ost?.TrainingDate}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="วันหมดอายุ Training"
                              variant="outlined"
                              placeholder="วันหมดอายุ Training"
                              value={
                                ost?.ExpireDate ? dayjs(ost.ExpireDate).format('DD/MM/YYYY') : ''
                              }
                              InputProps={{ readOnly: true }}
                              disabled={!ost?.ExpireDate}
                            />
                          </Grid>

                          {ost?.TrainingData?.map((element: any) => {
                            if (element?.JobData.length > 0) {
                              return (
                                <>
                                  <Grid item mt={2} xs={6}>
                                    <TextField
                                      fullWidth
                                      label="ตำแหน่งปฎิบัติงาน" // WorkType
                                      variant="outlined"
                                      placeholder="ตำแหน่งปฎิบัติงาน"
                                      value={element?.WorkType ? element?.WorkType : ''}
                                      InputProps={{ readOnly: true }}
                                      disabled={!ost?.TrainingDate}
                                    />
                                  </Grid>
                                  <Grid item xs={6}></Grid>
                                  {element?.JobData?.map((element_job: any) => {
                                    return (
                                      <>
                                        <Grid item xs={6}>
                                          <TextField
                                            fullWidth
                                            label="ตำแหน่งหน้างาน" // JobData-JobName
                                            variant="outlined"
                                            placeholder="ตำแหน่งหน้างาน"
                                            value={element_job?.JobName || ''}
                                            InputProps={{ readOnly: true }}
                                            disabled={!ost?.TrainingDate}
                                          />
                                        </Grid>
                                        <Grid item xs={6}>
                                          {element_job?.Document?.map((element_job_docs: any) => {
                                            return (
                                              <>
                                                <Tooltip title={element_job_docs?.FileName || ''}>
                                                  <IconButton
                                                    aria-label="description"
                                                    color="primary"
                                                    sx={{ '& svg': { fontSize: '2.5rem' } }}
                                                    onClick={() =>
                                                      window.open(
                                                        element_job_docs?.URLFile,
                                                        '_blank'
                                                      )
                                                    }
                                                  >
                                                    <DescriptionIcon />
                                                  </IconButton>
                                                </Tooltip>
                                              </>
                                            );
                                          })}
                                        </Grid>
                                      </>
                                    );
                                  })}
                                </>
                              );
                            } else {
                              return (
                                <>
                                  <Grid item mt={2} xs={6}>
                                    <TextField
                                      fullWidth
                                      label="ตำแหน่งปฎิบัติงาน" // WorkType
                                      variant="outlined"
                                      placeholder="ตำแหน่งปฎิบัติงาน"
                                      value={element?.WorkType ? element?.WorkType : ''}
                                      InputProps={{ readOnly: true }}
                                      disabled={!element?.WorkType}
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    {element?.Document?.map((element_docs: any) => {
                                      return (
                                        <>
                                          <Tooltip title={element_docs?.FileName || ''}>
                                            <IconButton
                                              aria-label="description"
                                              color="primary"
                                              sx={{ '& svg': { fontSize: '2.5rem' } }}
                                              onClick={() =>
                                                window.open(element_docs?.URLFile, '_blank')
                                              }
                                            >
                                              <DescriptionIcon />
                                            </IconButton>
                                          </Tooltip>
                                        </>
                                      );
                                    })}
                                  </Grid>
                                </>
                              );
                            }
                          })}
                        </Grid>
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </>
      )}
      <Dialog open={isOpenSuccessDialog} onClose={() => setIsOpenSuccessDialog(false)}>
        <DialogTitle>{'Success'}</DialogTitle>
        <DialogContent>
          <DialogContentText>อัพเดทข้อมูลการตรวจสอบและอัปเดตบุคคลสำเร็จ</DialogContentText>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OnlineSafetyTrainingPage;
